import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'src/app/shared/services/user.service';
import { AuthCognitoService } from '@app/services/auth-cognito.service';
import { ResourceService } from '@app/services/resources.service';
import { ThemeDisplayService } from '@app/services/theme-display.service';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Roles, User } from '@app/models/user';
import { ApplicationServiceStatus, ApplicationStatus } from '@app/models/monitoring';
import { LocalStorage, RegistrationTool, Segment } from '@app/models/constants';
import {
	ClrDatagridModule,
	ClrDropdownModule,
	ClrModalModule,
	ClrNavigationModule,
	ClrSpinnerModule
} from '@clr/angular';
import { CommonModule } from '@angular/common';
import { ToCamelPipe } from '@app/shared/pipes/to-camel.pipe';
import { CdsIconModule } from '@cds/angular';
import { environment } from '@env/environment';

const { version: appVersion } = require('../../../../../../package.json');

declare let require: any;

@Component({
	selector: 'app-top-menu',
	templateUrl: './application-top-menu.component.html',
	styleUrls: ['./application-top-menu.component.scss'],
	imports: [
		CommonModule,
		ClrNavigationModule,
		ClrDropdownModule,
		TranslateModule,
		ToCamelPipe,
		ClrModalModule,
		ClrSpinnerModule,
		ClrDatagridModule,
		CdsIconModule,
		RouterLink
	],
	standalone: true
})
export class ApplicationTopMenuComponent implements OnInit {
	public appVersion;
	userDetails: User;
	lang = 'fr';
	public readonly ENVIRONMENT = environment;

	openMonitoring = false;
	applicationStatus = { status: 'unknown' } as ApplicationStatus;
	statusRefresh = false;

	constructor(
		private router: Router,
		private toastr: ToastrService,
		public translate: TranslateService,
		private resourceService: ResourceService,
		public userService: UserService,
		private themeDisplayService: ThemeDisplayService,
		private authService: AuthCognitoService
	) {
		this.appVersion = appVersion;
	}

	async ngOnInit() {
		if (await this.authService.isAuthenticated()) {
			this.userDetails = this.userService.getUser();
			this.toastr.info('Cognito Session active', 'Welcome back ' + this.userDetails.firstName);

			if (this.userDetails.username && this.userDetails.username !== '') {
				localStorage.setItem('sAMAccountName', this.userDetails.username);
			}
		}
		if (localStorage.getItem(LocalStorage.MARKET) != null) {
			this.doChangeMarket(localStorage.getItem(LocalStorage.MARKET));
		} else {
			this.doChangeMarket(Segment.B2C);
		}
		if (localStorage.getItem(LocalStorage.LANGUAGE) != null) {
			this.translate.use(localStorage.getItem(LocalStorage.LANGUAGE));
			this.lang = localStorage.getItem(LocalStorage.LANGUAGE);
		}
	}

	hasRoles() {
		return this.userService.hasRole(Roles.BUSINESS_ADMIN) || this.userService.hasRole(Roles.INTERNAL_BACKOFFICE_EXPERT);
	}

	statusColor(item: ApplicationServiceStatus) {
		if ('CHECK_OK' === item.status) {
			return 'GREEN';
		} else if ('UNCHECKED' === item.status) {
			return 'ORANGE';
		}
		return 'RED';
	}

	async doLogout() {
		await this.authService.signOut();
		localStorage.removeItem('sAMAccountName');
	}

	doOpenAndRefreshStatus() {
		this.statusRefresh = true;
		this.resourceService.getApplicationStatus().subscribe((value) => {
			this.applicationStatus = value;
			this.statusRefresh = false;
		});
		this.openMonitoring = true;
	}

	doChangeMarket(market: string) {
		if (this.currentMarket() !== market) {
			const currentTool: string = this.currentTool();
			this.themeDisplayService.displayMarketTheme(market, currentTool);
			this.router.navigate([`/${currentTool}/`]);
		}
	}

	doChangeLang(language: string) {
		this.lang = language;
		localStorage.setItem('language', language);
		this.translate.use(language);
	}

	currentMarket(): string {
		return localStorage.getItem(LocalStorage.MARKET) != null ? localStorage.getItem(LocalStorage.MARKET) : Segment.B2C;
	}

	currentTool(): string {
		return localStorage.getItem(LocalStorage.TOOLS) != null
			? localStorage.getItem(LocalStorage.TOOLS)
			: RegistrationTool.REGISTRATIONS;
	}

	async getJWTToken() {
		const currentSession: CognitoUserSession = await this.authService.getCurrentSession();
		const token = currentSession.getIdToken().getJwtToken();
		await navigator.clipboard.writeText(token);
		this.toastr.info('JWT Token copied to clipboard');
	}
}
